// File: src/components/EventLiveEntrySheet.jsx
import React, { useRef, useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import mixerApi from '../utils/mixerApi';
import { AuthContext } from '../context/AuthContext';
import { LuInfo } from 'react-icons/lu';

/* -------------------- UI tokens (match Registration/Update + SortBy) -------------------- */
const COLORS = {
  bg: '#ffffff',
  text: '#0b0b0b',
  subtext: '#525252',
  border: '#EEF0F2',
  brand: '#0b0b0b',
  overlay: 'rgba(0,0,0,0.4)',
  chipBg: '#F4F4F5',
};

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

/**
 * Props:
 * - event:                full event object (preferred)
 * - eventId:              string; mixers may be "mixer-<id>"
 * - requiresRegistration: boolean (capacity > 0)           // optional override
 * - isRegistered:         boolean                          // optional override
 * - eventStarted:         boolean                          // true at actual start time
 *
 * Legacy/optional display props:
 * - galleryImages, eventName, hostName
 *
 * Actions:
 * - onCancel:    () => void
 * - onContinue:  () => void   // optional override; if present, bypasses built-in routing
 */
export default function EventLiveEntrySheet({
  event = null,
  eventId = '',
  requiresRegistration, // optional explicit
  isRegistered,         // optional explicit
  eventStarted = false,

  galleryImages = [],
  eventName = '(x)',
  hostName = '',
  onCancel,
  onContinue, // optional override; if provided, we call it instead of built-in routing
}) {
  const [focusIndex, setFocusIndex] = useState(0);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // Hooks must be unconditionally called
  const { currentUser } = useContext(AuthContext);

  /* -------------------- Center-on-scroll (same behavior as EventCard) -------------------- */
  function onScrollGallery() {
    const container = scrollRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const center = rect.left + rect.width / 2;

    let minDist = Infinity;
    let newFocus = 0;

    const children = container.children || [];
    for (let i = 0; i < children.length; i++) {
      const cRect = children[i].getBoundingClientRect();
      const cCenter = cRect.left + cRect.width / 2;
      const dist = Math.abs(cCenter - center);
      if (dist < minDist) {
        minDist = dist;
        newFocus = i;
      }
    }
    setFocusIndex(newFocus);
  }

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    scroller.addEventListener('scroll', onScrollGallery, { passive: true });
    return () => scroller.removeEventListener('scroll', onScrollGallery);
  }, []);

  // ── After ALL hooks, do your early returns (prevents hooks-order violations)
  const isAnyModerationActive =
    currentUser?.isBanned || currentUser?.isTempBanned || currentUser?.hasWarning;

  // Only show for logged-in users
  if (!currentUser?._id) return null;

  /* -------------------- Derive registration gate (fallbacks) -------------------- */
  const userId =
    currentUser?._id ||
    localStorage.getItem('userId') ||
    '';

  // if the parent provided an explicit requiresRegistration, keep it; otherwise infer from event
  const computedRequiresRegistration =
    typeof requiresRegistration === 'boolean'
      ? requiresRegistration
      : (Number(event?.capacity ?? 0) > 0);

  // helper: loose id equality
  const eq = (a, b) => {
    if (!a || !b) return false;
    const as = a?.toString ? a.toString() : String(a);
    const bs = b?.toString ? b.toString() : String(b);
    return as === bs;
  };

  // derive "isRegistered" from event.attendeeIds / event.attendees if not passed
  const inferredIsRegistered = (() => {
    if (!event || !userId) return false;
    const ids = Array.isArray(event.attendeeIds) ? event.attendeeIds : [];
    const users = Array.isArray(event.attendees) ? event.attendees : [];
    if (ids.some((id) => eq(id, userId))) return true;
    if (users.some((u) => eq(u?._id ?? u, userId))) return true;
    return false;
  })();

  const computedIsRegistered =
    typeof isRegistered === 'boolean' ? isRegistered : inferredIsRegistered;

  // Registration/moderation gates
  // Registration/moderation gates
// ⛔️ Always require registration before showing this sheet.
if (!computedIsRegistered) return null;
if (isAnyModerationActive) return null;

  /* -------------------- Continue routing (matches EventCardPage.handleEnter) -------------------- */
  const builtInContinue = async () => {
    // Respect custom handler if provided
    if (typeof onContinue === 'function') {
      onContinue();
      return;
    }

    const evt = event || {};
    const rawId =
      (eventId && String(eventId)) ||
      (evt?._id?.toString?.() || (evt?._id != null ? String(evt._id) : ''));

    const isMixer =
      evt?.type === 'mixer' ||
      evt?.mode === 'mixer' ||
      (rawId ? rawId.startsWith('mixer-') : false);

    // ✅ Detect Neutral (by mode OR type)
    const isNeutral =
      String(evt?.mode || '').toLowerCase() === 'neutral' ||
      String(evt?.type || '').toLowerCase() === 'neutral';

    const cleanId = isMixer ? rawId.replace(/^mixer-/, '') : rawId;

    // ── Neutral → intro wizard, then portal (no quiz)
    if (isNeutral) {
      navigate(`/neutral/${rawId}/intro`, { state: { event: evt } });
      return;
    }

    if (isMixer) {
      try {
        const uid = userId;
        const { data: quizData } = await mixerApi.fetchQuiz(cleanId, uid);
        if (quizData?.alreadySubmitted) {
          // direct to mixer portal if quiz already submitted
          navigate(`/mixer/events/${cleanId}`, { state: { event: evt } });
        } else {
          // otherwise, mixer intro wizard
          navigate('/intro-mixer', { state: { event: evt } });
        }
      } catch (err) {
        // If quiz check fails, fall back to the mixer intro to avoid dead-ends
        navigate('/intro-mixer', { state: { event: evt } });
      }
      return;
    }

    // Standard events → intro wizard
    navigate('/intro', { state: { event: evt } });
  };

  // display fallbacks from event when callers omit props
  const displayGallery = (galleryImages?.length ? galleryImages : (event?.galleryImages || [])) || [];
  const displayEventName = eventName && eventName !== '(x)' ? eventName : (event?.name || '(x)');
  const displayHostName = hostName || event?.hostedAt || '';

  return (
    <div
      style={styles.backdrop}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Inside event radius"
    >
      <div
        style={{
          ...styles.sheet,
          marginBottom: `calc(${isPWA() ? 16 : 8}px + env(safe-area-inset-bottom, 0px))`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (aligns with Sort By: title + bottom border) */}
        <div style={styles.header}>
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    }}
  >
    <span style={styles.headerTitle}>
      {eventStarted ? 'Gathering is live' : 'Portal is open'}
    </span>

    <div style={styles.livePill}>
      <span style={styles.liveDot} />
      <span>{eventStarted ? 'LIVE' : 'LIVE SOON'}</span>
    </div>
  </div>
</div>

        {/* Horizontal gallery */}
        {!!displayGallery.length && (
          <div style={styles.galleryWrap}>
            <div ref={scrollRef} style={styles.galleryScroll} className="hide-scrollbar">
              {displayGallery.map((url, i) => {
                const isFocus = i === focusIndex;
                return (
                  <img
                    key={`gallery-${i}`}
                    src={url}
                    alt={`event-gallery-${i}`}
                    style={{
                      ...styles.galleryImg,
                      transform: isFocus ? 'scale(1)' : 'scale(0.88)',
                      opacity: isFocus ? 1 : 0.75,
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div style={styles.content}>
          <h2 style={styles.heading}>You’re registered for</h2>
          <h3 style={styles.eventName}>{displayEventName}</h3>

          {displayHostName ? (
            <p style={styles.hostLine}>
              Hosted at <strong>{displayHostName}</strong>
            </p>
          ) : null}

          <div style={styles.infoCard}>
            <div style={styles.infoIconWrap}>
              <LuInfo size={17} strokeWidth={2.2} />
            </div>

            <p style={styles.subtitle}>
              {eventStarted
                ? 'This event is happening now. If you’re at the address, enter the portal and let the group know where you are.'
                : 'Your gathering starts soon. The portal is now open so you can connect with the group before it begins.'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button type="button" onClick={onCancel} style={styles.secondaryBtn}>
            Not now
          </button>
          <button type="button" onClick={builtInContinue} style={styles.primaryBtn}>
            Continue
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>
        {`
          @keyframes sheetEnter {
            from { transform: translateY(16px); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            * { animation: none !important; transition: none !important; }
          }
          button:focus-visible {
            outline: 0;
            box-shadow: 0 0 0 3px rgba(59,130,246,.45);
          }
        `}
      </style>
    </div>
  );
}

/* -------------------- Styles -------------------- */
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: COLORS.overlay,
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 500,
    margin: '0 10px',
    backgroundColor: COLORS.bg,
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'sheetEnter .25s ease-out',
    border: `1px solid ${COLORS.border}`,
  },

  /* Header (mirrors SortBy header) */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    background: '#fff',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: COLORS.text,
    letterSpacing: '-0.01em',
  },

  /* Gallery */
  galleryWrap: {
    padding: '8px 12px 0',
    background: '#fff',
  },
  galleryScroll: {
    display: 'flex',
    gap: 10,
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 6,
  },
  galleryImg: {
    width: 220,
    height: 140,
    objectFit: 'cover',
    borderRadius: 12,
    scrollSnapAlign: 'center',
    transition: 'transform 220ms ease, opacity 220ms ease',
    flexShrink: 0,
    border: `1px solid ${COLORS.border}`,
    background: '#f8f8f8',
  },

  /* Content */
  content: {
    textAlign: 'center',
    padding: '50px 24px 28px',
    background: '#fff',
  },
  badge: {
    display: 'inline-block',
    backgroundColor: COLORS.chipBg,
    color: COLORS.text,
    fontWeight: 800,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    letterSpacing: '0.02em',
    marginBottom: 8,
  },
  heading: {
    fontSize: '0.76rem',
    fontWeight: 700,
    color: COLORS.subtext,
    margin: '0 0 10px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  eventName: {
    fontSize: '1.25rem',
    fontWeight: 900,
    color: COLORS.text,
    margin: 0,
  },
  hostLine: {
    margin: '12px 0 0',
    fontSize: '0.82rem',
    lineHeight: 1.35,
    color: COLORS.subtext,
  },
  infoCard: {
    maxWidth: 390,
    margin: '22px auto 0',
    padding: '14px 16px',
    borderRadius: 12,
    background: '#F5F5F5',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    textAlign: 'left',
  },
  infoIconWrap: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#E9E9E9',
    color: '#3F3F46',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  subtitle: {
    fontSize: '0.86rem',
    color: COLORS.subtext,
    margin: 0,
    lineHeight: 1.5,
  },

  /* Footer actions */
  actions: {
    display: 'flex',
    gap: 10,
    padding: 16,
    borderTop: `1px solid ${COLORS.border}`,
    background: '#fff',
  },
  secondaryBtn: {
    flex: 1,
    borderRadius: 10,
    border: `1px solid ${COLORS.text}`,
    background: '#fff',
    color: COLORS.text,
    fontWeight: 800,
    fontSize: 12,
    padding: '12px 0',
    cursor: 'pointer',
  },
  primaryBtn: {
    flex: 1,
    borderRadius: 10,
    border: '1px solid transparent',
    background: COLORS.brand,
    color: '#fff',
    fontWeight: 800,
    fontSize: 12,
    padding: '12px 0',
    cursor: 'pointer',
  },
  livePill: {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 999,
  backgroundColor: '#E8F5E9',
  color: '#1B5E20',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
},

liveDot: {
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#22C55E',
  animation: 'livePulse 1.6s ease-in-out infinite',
},

};
